import { useEffect, useState } from "react"
import { Download, WrapText } from "lucide-react"
import { api, mediaUrl } from "../../api/client"
import { Button, Modal, Spinner, cx } from "../ui"
import { RichBody } from "../editor/RichBody"
import { fileSize } from "../../lib/format"

/**
 * Read a text attachment without downloading it.
 *
 * The text comes through the API rather than from the file's own URL, which in
 * production is on S3 and would need CORS to be read by script. HTML is shown
 * in a sandbox with nothing allowed - no script, no same-origin, no forms - so
 * a file is only ever looked at, never run.
 */

const MARKDOWN = ["md", "markdown"]
const HTML = ["html", "htm"]
const TEXT = [
	"txt",
	"log",
	"json",
	"csv",
	"py",
	"js",
	"jsx",
	"ts",
	"tsx",
	"css",
	"sql",
	"yaml",
	"yml",
	"sh",
	"diff",
	"patch"
]

function extension(filename) {
	const match = /\.([a-z0-9]+)$/i.exec(filename || "")
	return match ? match[1].toLowerCase() : ""
}

function previewKind(filename) {
	const ext = extension(filename)
	if (MARKDOWN.includes(ext)) return "markdown"
	if (HTML.includes(ext)) return "html"
	if (TEXT.includes(ext)) return "text"
	return null
}

export function canPreview(file) {
	return Boolean(file && !file.is_image && previewKind(file.filename))
}

export function AttachmentPreview({ file, onClose }) {
	const [text, setText] = useState(null)
	const [error, setError] = useState(null)
	const [wrap, setWrap] = useState(true)
	const kind = previewKind(file?.filename)

	useEffect(() => {
		if (!file) return undefined
		let cancelled = false
		setText(null)
		setError(null)
		api.attachmentText(file.attachment_id)
			.then((body) => {
				if (!cancelled) setText(body || "")
			})
			.catch((cause) => {
				if (cancelled) return
				setError(
					cause.code === "preview_too_large"
						? "This file is too large to preview. Download it to read it."
						: cause.code === "not_previewable"
							? "This file cannot be previewed. Download it instead."
							: cause.message
				)
			})
		return () => {
			cancelled = true
		}
	}, [file])

	return (
		<Modal
			open={Boolean(file)}
			onClose={onClose}
			title={file?.filename || "Preview"}
			description={file ? fileSize(file.size_bytes) : undefined}
			size="lg"
			footer={
				<>
					{kind === "text" && text !== null && (
						<Button
							size="sm"
							variant="ghost"
							icon={WrapText}
							aria-pressed={wrap}
							onClick={() => setWrap((on) => !on)}
							className="mr-auto"
						>
							{wrap ? "No wrap" : "Wrap lines"}
						</Button>
					)}
					<Button
						as="a"
						size="sm"
						icon={Download}
						href={file ? mediaUrl(file.url) : undefined}
						target="_blank"
						rel="noreferrer"
					>
						Download
					</Button>
					<Button size="sm" variant="primary" onClick={onClose}>
						Close
					</Button>
				</>
			}
		>
			{error ? (
				<p className="text-[0.8125rem] leading-relaxed text-ink-600">{error}</p>
			) : text === null ? (
				<Spinner label="Loading preview" />
			) : kind === "markdown" ? (
				<div className="max-h-[70vh] overflow-y-auto scrollbar-slim">
					<RichBody body={text} format="markdown" />
				</div>
			) : kind === "html" ? (
				<iframe
					title={file.filename}
					sandbox=""
					srcDoc={text}
					className="h-[70vh] w-full rounded-md border border-ink-200 bg-white"
				/>
			) : (
				<pre
					className={cx(
						"max-h-[70vh] overflow-auto rounded-md bg-ink-50 p-3 font-mono text-xs leading-relaxed text-ink-800 scrollbar-slim",
						wrap ? "whitespace-pre-wrap break-words" : "whitespace-pre"
					)}
				>
					{text}
				</pre>
			)}
		</Modal>
	)
}
